import {
  IconBellExclamation,
  IconChecklist,
  IconClipboardCheck,
  IconCpu,
  IconTool,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { useFitColumns } from '../../fit-columns.js';
import { formatDay } from '../../format.js';
import { Panel } from '../../Panel.js';
import type { OverviewCounts } from './equipment-tree.js';
import classes from './Pms.module.css';
import work from './PmsWork.module.css';
import { PmsBadge, RowsSkeleton } from './PmsWorkParts.js';
import { frequencyLabel, type OverviewRow } from './plan-view.js';
import type { EquipmentNode } from './types.js';

const COLUMNS = [
  'component',
  'type',
  'workType',
  'frequency',
  'lastDone',
  'nextDue',
  'status',
] as const;

/**
 * Dar tabloda gizlenme sırası (`fit-columns.ts`): önce düğüm türü, sonra sıklık, son yapılış ve iş
 * türü. Bileşen, sonraki vade ve durum hiç gizlenmez; ayrıntılar Component Details'ta.
 */
const DROP: Partial<Record<(typeof COLUMNS)[number], number>> = {
  type: 1,
  frequency: 2,
  lastDone: 3,
  workType: 4,
};

function Tile({
  icon,
  label,
  value,
  tone,
}: {
  icon: ReactNode;
  label: string;
  value: number;
  tone: 'success' | 'info' | 'warning' | 'neutral';
}) {
  return (
    <div className={classes.tile} data-tone={tone}>
      <span className={classes.tileIcon} aria-hidden>
        {icon}
      </span>
      <span className={classes.tileBody}>
        <span className={classes.tileLabel}>{label}</span>
        <span className={classes.tileValue}>{value}</span>
      </span>
    </div>
  );
}

export interface EquipmentOverviewProps {
  /** Seçili düğümün adı; seçim yoksa `null`. */
  name: string | null;
  counts: OverviewCounts;
  /** Seçilenin alt ağacındaki işler (`overviewRows`); `null`: okunuyor. */
  rows?: readonly OverviewRow[] | null;
  nodes?: readonly EquipmentNode[];
  /** Satır menüsü ("…": View, Create Work Order, Postpone; kapsama göre). */
  rowActions?: (row: OverviewRow) => ReactNode;
}

/**
 * "<Ad> – Equipment Overview" (`gemi-pms-ekipman.jpeg`): dört özet ve iş tablosu (Component, Type,
 * Work Type, Frequency, Last Done, Next Due, Status, "…"). Sayıları kod hesaplar (ADR 0024 #19);
 * vade ve durum plan motorundan (K39). İş yokken tasarlanmış boş durum.
 */
export function EquipmentOverview({
  name,
  counts,
  rows,
  nodes = [],
  rowActions,
}: EquipmentOverviewProps) {
  const { t, i18n } = useTranslation();
  const tableBox = useFitColumns<HTMLElement>();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pms.equipment.overview.${key}`, values);
  const language = i18n.resolvedLanguage ?? 'en';
  const byId = new Map(nodes.map((n) => [n.id, n]));
  return (
    <Panel
      title={name ? k('title', { name }) : k('titleNone')}
      className={[classes.fill, classes.overviewPanel].join(' ')}
      aria-label={k('titleNone')}
    >
      <div className={classes.tiles}>
        <Tile
          icon={<IconCpu size={22} stroke={1.7} />}
          label={k('totalComponents')}
          value={counts.totalComponents}
          tone="success"
        />
        <Tile
          icon={<IconClipboardCheck size={22} stroke={1.7} />}
          label={k('pmDue')}
          value={counts.due}
          tone="info"
        />
        <Tile
          icon={<IconBellExclamation size={22} stroke={1.7} />}
          label={k('overdue')}
          value={counts.overdue}
          tone="warning"
        />
        <Tile
          icon={<IconChecklist size={22} stroke={1.7} />}
          label={k('completed')}
          value={counts.completed}
          tone="neutral"
        />
      </div>
      <section
        ref={tableBox}
        className={classes.tableBox}
        aria-label={t('pms.equipment.tableOf', { name: k('titleNone') })}
        data-fill={!rows || rows.length === 0 || undefined}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
        tabIndex={0}
      >
        <table className={classes.table} data-cards>
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th key={column} scope="col" data-drop={DROP[column]}>
                  {k(`columns.${column}`)}
                </th>
              ))}
              <th scope="col" className={classes.actionsHead}>
                <span className={classes.srOnly}>{k('columns.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows === null ? (
              <tr>
                <td colSpan={COLUMNS.length + 1} className={classes.emptyCell}>
                  <RowsSkeleton rows={5} />
                </td>
              </tr>
            ) : !rows || rows.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length + 1} className={classes.emptyCell}>
                  <EmptyState
                    icon={<IconTool size={22} stroke={1.8} />}
                    title={k('empty')}
                    description={k('emptyHint')}
                  />
                </td>
              </tr>
            ) : (
              rows.map(({ job, plan }) => {
                const node = byId.get(job.equipmentId);
                return (
                  <tr key={job.id}>
                    <td data-label={k('columns.component')} title={job.title}>
                      {node?.name ?? '—'}
                    </td>
                    <td data-label={k('columns.type')} data-drop={DROP.type}>
                      {node ? t(`pms.nodeTypes.${node.nodeType}`) : '—'}
                    </td>
                    <td data-label={k('columns.workType')} data-drop={DROP.workType}>
                      {t(`pms.work.workTypes.${job.workType}`, { defaultValue: job.workType })}
                    </td>
                    <td data-label={k('columns.frequency')} data-drop={DROP.frequency}>
                      {frequencyLabel(plan?.effectiveInterval ?? job.interval, t, language)}
                    </td>
                    <td data-label={k('columns.lastDone')} data-drop={DROP.lastDone}>
                      {formatDay(plan?.lastDone.doneOn)}
                    </td>
                    <td data-label={k('columns.nextDue')}>{formatDay(plan?.due.dueOn)}</td>
                    <td data-label={k('columns.status')}>
                      {plan ? <PmsBadge status={plan.status} /> : '—'}
                    </td>
                    <td className={work.actionsCell}>
                      {rowActions ? rowActions({ job, plan }) : null}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </section>
    </Panel>
  );
}
